import { type GlobalRole } from "interfaces/role";
import { useCallback, useEffect, useMemo } from "react";
import { Label } from "semantic-ui-react";
import FormSectionHeader from "components/forms/FormSectionHeader";
import type ValidatedFormProps from "../../../../components/forms/validatedFormProps";
import type NewContact from "../../../../interfaces/NewContact";
import AddContact from "../Contacts/AddContact/AddContact";
import ManagedByContactList from "./ManagedByContactList";
import styles from "./ManagedByContacts.module.scss";

export interface ManagedByContactsProps extends ValidatedFormProps {
  contacts: NewContact[];
  onContactsChanged: (contacts: Array<NewContact>) => void;
  childRoles: GlobalRole[];
  accountId: number;
  isLoadingChildRoles: boolean;
  fetchChildAccountRoles(accountId: number): void;
  moboAccountId: number;
}

const groupOwnerRole: GlobalRole = {
  id: 0,
  name: "Group Owner",
  description: "Manage specific Inherited Groups.",
  isMainContactRole: true,
};
const initialSelectedRoles = [groupOwnerRole];

const ManagedByContacts = ({
  contacts = [],
  onContactsChanged,
  childRoles,
  accountId,
  isLoadingChildRoles,
  fetchChildAccountRoles,
  moboAccountId,
}: ManagedByContactsProps) => {
  useEffect(() => {
    fetchChildAccountRoles(accountId);
  }, [fetchChildAccountRoles, accountId]);

  const roles = useMemo(() => [...childRoles, groupOwnerRole], [childRoles]);

  const addContact = useCallback(
    (contact: NewContact) => {
      const newContact = { ...contact, id: contacts.length, roles: initialSelectedRoles };
      onContactsChanged([...contacts, newContact]);
    },
    [contacts, onContactsChanged],
  );

  const removeContact = useCallback(
    (contactId: number) => {
      const updated = contacts
        .filter((c) => c.id !== contactId)
        .map((c, idx) => ({
          ...c,
          id: idx,
        }));
      onContactsChanged(updated);
    },
    [contacts, onContactsChanged],
  );

  const badge = useMemo(
    () =>
      contacts.length ? (
        <Label circular color="blue" className={styles["badge"]}>
          {contacts.length}
        </Label>
      ) : null,
    [contacts.length],
  );

  return (
    <section className={styles["contact-form"]}>
      <FormSectionHeader title="Add contacts" />
      <AddContact
        contacts={contacts}
        onContactAdded={addContact}
        globalRoles={roles}
        selectedRoles={initialSelectedRoles}
        isLoadingGlobalRoles={isLoadingChildRoles}
        isReadOnly
      />
      <div className={styles["contacts-overview"]}>
        <div className={styles["title-container"]}>
          <FormSectionHeader title="Contacts" />
          {badge}
        </div>
        <ManagedByContactList
          contacts={contacts}
          onContactRemoved={removeContact}
          accountId={accountId}
          moboAccountId={moboAccountId}
        />
      </div>
    </section>
  );
};

export default ManagedByContacts;
